import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Select } from 'antd';
import ErrorText from '../../components/errorText';
import {
  LeadListTriggerProps,
  LeadListTriggerData,
  TRIGGER_OPTIONS,
  SOURCE_LABELS,
  ERROR_MESSAGES,
  SourceData,
  ContentItem,
  UserPreferences,
  ApiResponse,
} from './LeadListTriggerTypes';
import {
  getAllIntegration,
  getFilePageContent,
} from '../../services/automationService';
import logger from '../../utils/logger';
import '../automation/TriggerSelectionPopup.css'; // For multi-select dropdown styles

/**
 * LeadListTriggerSection Component
 * Handles trigger selection and configuration for lead list automation
 */
const LeadListTriggerSection: React.FC<LeadListTriggerProps> = ({
  onChange,
  initialData = { event: '', targetValues: '' },
  isLoading: parentLoading = false,
}) => {
  // Local state
  const [triggerData, setTriggerData] = useState<LeadListTriggerData>(initialData);
  const [isShowAutoEvent, setIsShowAutoEvent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Data sources state
  const [sourceByData, setSourceByData] = useState<SourceData[]>([]);
  const [viewPageData, setViewPageData] = useState<ContentItem[]>([]);
  const [viewFileData, setViewFileData] = useState<ContentItem[]>([]);

  // Get user preferences - use Redux with localStorage fallback
  const reduxPreferences = useSelector((state: any) => {
    return state?.rootReducers?.user?.userPreferences;
  });

  const userPreferences = useMemo(() => {
    // If Redux has valid data (not empty array), use it
    if (reduxPreferences && !Array.isArray(reduxPreferences) && reduxPreferences.status) {
      return reduxPreferences as UserPreferences;
    }
    // Fallback to localStorage (same pattern as leads-renderers.tsx)
    try {
      const stored = JSON.parse(localStorage.getItem("user_preferences") || "{}");
      return stored as UserPreferences;
    } catch {
      return {} as UserPreferences;
    }
  }, [reduxPreferences]);

  // Get custom activity types
  const customActivityType = useMemo(() => {
    return userPreferences?.customActivityType || [];
  }, [userPreferences]);

  // Memoized merged sources including custom sources
  const allSources = useMemo(() => {
    if (!userPreferences?.customSource) return sourceByData;
    return [
      ...sourceByData,
      ...userPreferences.customSource.map(cs => ({
        _id: cs.value,
        name: cs.name,
        isActive: cs.isActive
      }))
    ].filter(source => source.isActive);
  }, [sourceByData, userPreferences?.customSource]);

  const needsCondition = (eventType: string): boolean => {
    const eventsWithoutConditions = [
      'edit_lead',
      'quotation_created',
      'quotation_edited',
      'incoming_call_activity',
      'outgoing_call_activity',
      'missed_call_activity'
    ];
    return !eventsWithoutConditions.includes(eventType);
  };

  /**
   * Fetches integration sources
   */
  // In LeadListTriggerSection.tsx
  const fetchSourceData = useCallback(async () => {
    try {
      setLoading(true);
      const axiosResponse = await getAllIntegration({
        isAscending: false,
        page: 1,
        perPage: 50, // Increased to ensure all integrations are fetched
      });
      
      // Transform Axios response to match our ApiResponse type
      const response: ApiResponse<SourceData[]> = {
        status: axiosResponse.status === 200,
        data: axiosResponse.data
      };
      
      if (response.status) {
        // Filter integrations that are both active and displayable
        const filteredSources = response.data.data.filter(
          (source: SourceData) => 
            source.isActive === true && 
            source.isDisplayable === true
        );
        
        // Preserve the existing transformation logic
        setSourceByData(filteredSources);
      }
    } catch (err) {
      logger.error('Error fetching source data:', err);
      setError(ERROR_MESSAGES.FAILED_LOAD);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetches page/file content based on type
   */
  const fetchContentData = useCallback(async (type: 'page' | 'file') => {
    try {
      setLoading(true);
      const response = await getFilePageContent({
        isAscending: false,
        page: 1,
        perPage: 15,
        type
      });

      if (response?.status) {
        if (type === 'page') {
          setViewPageData(response.data.data);
        } else {
          setViewFileData(response.data.data);
        }
      }
    } catch (err) {
      logger.error(`Error fetching ${type} content:`, err);
      setError(ERROR_MESSAGES.FAILED_LOAD);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data load on mount
  useEffect(() => {
    fetchSourceData();
  }, [fetchSourceData]);

  // Load content data when specific triggers are selected
  useEffect(() => {
    if (triggerData.event === 'view_page') {
      fetchContentData('page');
    } else if (triggerData.event === 'view_file') {
      fetchContentData('file');
    }
    // Removed empty block that was causing issues
  }, [triggerData.event, fetchContentData]);

  // Update from initial data changes
  useEffect(() => {
    setTriggerData(initialData);
    setIsShowAutoEvent(!!initialData.event);
    setError('');
  }, [initialData]);

  // Parse targetValues into string[] for the multi-select
  const selectedValues: string[] = useMemo(() => {
    const tv = triggerData.targetValues;
    if (!tv) return [];
    if (Array.isArray(tv)) return tv;
    if (typeof tv === 'string' && tv.length > 0) return tv.split(',');
    return [];
  }, [triggerData.targetValues]);

  // Build options list based on event type
  const targetOptions = useMemo(() => {
    const event = triggerData.event;
    if (!event) return [];

    logger.log('Building targetOptions for event:', event, {
      statusCount: userPreferences?.status?.length,
      labelsCount: userPreferences?.labels?.length,
      sourcesCount: allSources?.length,
      userPreferences: userPreferences
    });

    const options: { label: string; value: string }[] = [];

    switch (event) {
      case 'create_lead':
        (allSources || []).forEach((source: any) => options.push({ label: source.name, value: source._id }));
        options.push({ label: 'Any', value: 'any' });
        break;
      case 'status_change':
        (userPreferences?.status || []).forEach((item: any) => options.push({ label: item.name, value: item.value }));
        options.push({ label: 'Any', value: 'any' });
        break;
      case 'label_change':
        (userPreferences?.labels || []).forEach((item: any) => options.push({ label: item.name, value: item.value }));
        options.push({ label: 'Any', value: 'any' });
        break;
      case 'view_page':
        (viewPageData || []).forEach((item: any) => options.push({ label: item.details?.title, value: item._id }));
        options.push({ label: 'Any', value: 'any' });
        break;
      case 'view_file':
        (viewFileData || []).forEach((item: any) => options.push({ label: item.details?.title, value: item._id }));
        options.push({ label: 'Any', value: 'any' });
        break;
      case 'create_task':
        (userPreferences?.taskType || []).forEach((item: any) => options.push({ label: item.name, value: item.value }));
        options.push({ label: 'Any', value: 'any' });
        break;
      case 'edit_task':
        options.push({ label: 'Due date is updated', value: 'toBePerformAt' });
        options.push({ label: 'Task is completed', value: 'isCompleted' });
        options.push({ label: 'Any', value: 'any' });
        break;
      case 'create_activity': {
        const allActivities = [...(userPreferences?.activityType || []), ...(customActivityType || [])];
        allActivities.forEach((item: any) => options.push({ label: item.name, value: item.value }));
        options.push({ label: 'Any', value: 'any' });
        break;
      }
    }

    return options;
  }, [triggerData.event, allSources, userPreferences, viewPageData, viewFileData, customActivityType]);

  /**
   * Handles changes in trigger event selection (the first dropdown)
   */
  const handleTriggerChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    const updatedTriggerData = {
      ...triggerData,
      [name]: value,
      isValid: true,
    };

    if (name === 'event') {
      updatedTriggerData.targetValues = '';
      setIsShowAutoEvent(!!value);
    }

    setTriggerData(updatedTriggerData);
    setError('');
    onChange(updatedTriggerData);
  }, [triggerData, onChange]);

  /**
   * Handles multi-select target value changes
   */
  const handleTargetValuesChange = useCallback((values: string[]) => {
    const prevHadAny = selectedValues.includes('any');
    const nowHasAny = values.includes('any');

    let finalValues = values;
    if (nowHasAny && !prevHadAny) {
      // User just clicked "Any" — select all specific options
      finalValues = targetOptions
        .filter(opt => opt.value !== 'any')
        .map(opt => opt.value);
    } else if (prevHadAny && !nowHasAny) {
      finalValues = values.filter(v => v !== 'any');
    } else {
      finalValues = values.filter(v => v !== 'any');
    }

    logger.log('Target values changed (multi):', finalValues);

    const updatedTriggerData = {
      ...triggerData,
      targetValues: finalValues,
      isValid: true,
    };

    setTriggerData(updatedTriggerData);
    setError('');
    onChange(updatedTriggerData);
  }, [triggerData, onChange, selectedValues, targetOptions]);

  /**
   * Handles clearing/resetting the trigger rule
   */
  const handleClearTrigger = useCallback(() => {
    const clearedData = {
      event: '',
      targetValues: '',
      isValid: true
    };
    setTriggerData(clearedData);
    setIsShowAutoEvent(false);
    setError('');
    onChange(clearedData);
  }, [onChange]);

  const isLoadingState = loading || parentLoading;

  return (
    <div className="col-md-12 when_then">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center">
          <span className="auto_dot">
            <label className="dot_no">1</label>
          </span>
          <label className="when_then_label mb-0">When</label>
        </div>
        {triggerData.event && (
          <button
            type="button"
            onClick={handleClearTrigger}
            disabled={isLoadingState}
            className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
            style={{ fontSize: '12px', padding: '4px 8px' }}
            title="Clear automation rule"
          >
            <i className="bi bi-x-circle"></i>
            Clear Rule
          </button>
        )}
      </div>
      <span className="when_then_trigger d-block mb-3">
        Select a trigger to start your list automation
      </span>

      <div className="col-md-12">
        {/* Trigger Selection */}
        <div className="col-md-4 auto_select">
          <select
            name="event"
            value={triggerData.event}
            className="form-select"
            onChange={handleTriggerChange}
            disabled={isLoadingState}
          >
            <option value="">Select trigger</option>
            {TRIGGER_OPTIONS.map((option, i) => (
              <option value={option.value} key={i}>
                {option.name}
              </option>
            ))}
          </select>
        </div>

        {/* Target Value Selection */}
       {isShowAutoEvent && needsCondition(triggerData.event) && (
  <div className="col-md-8 condition-group" style={{ marginTop: 12 }}>
    <div className="d-flex align-items-center gap-2 mb-2">
      <span className="and">AND</span>
      <span className="and_source">
        {SOURCE_LABELS[triggerData.event] || ''}
      </span>
    </div>
    <Select
      mode="multiple"
      className="lead-list-multi-select"
      placeholder="Click to select one or more values..."
      value={selectedValues.length > 0 ? selectedValues : []}
      onChange={handleTargetValuesChange}
      disabled={isLoadingState}
      options={targetOptions}
      popupClassName="trigger-multi-select-dropdown"
      style={{ width: '100%' }}
      maxTagCount={5}
      maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
      allowClear
      showSearch
      filterOption={(input, option) =>
        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
      }
      listHeight={280}
      getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
    />
  </div>
)}

        {/* Error Display */}
        {error && <ErrorText message={error} />}
        
        {/* Loading Indicator */}
        {isLoadingState && (
          <div className="mt-2 text-muted">
            Loading...
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadListTriggerSection